if (!customElements.get('video-section-player')) {
  const isSafari =
    typeof navigator !== 'undefined' &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Browsers only keep a handful of videos decoded at once. Keep the nearest
  // few warm and release the rest so later videos can still play.
  const VideoWarmup = {
    players: new Set(),
    // Safari is stricter — keep fewer warm, and never hard-reset sources
    MAX_WARM: isSafari ? 3 : 4,
    raf: null,

    register(player) {
      this.players.add(player);
      this.schedule();
    },

    unregister(player) {
      this.players.delete(player);
    },

    schedule() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.reconcile();
      });
    },

    distance(player) {
      if (player.hasStarted && typeof player.isFullyOffScreen === 'function' && !player.isFullyOffScreen()) {
        return -1;
      }
      const rect = player.getBoundingClientRect();
      const viewMid = (window.innerHeight || 0) / 2;
      const mid = rect.top + rect.height / 2;
      return Math.abs(mid - viewMid);
    },

    reconcile() {
      const ranked = Array.from(this.players).sort((a, b) => this.distance(a) - this.distance(b));
      const viewH = window.innerHeight || 800;

      ranked.forEach((player, index) => {
        if (index < this.MAX_WARM) {
          player.warmUp?.();
          return;
        }

        // Hard release breaks Safari media elements — soft cool only there
        const farAway = !isSafari && this.distance(player) > viewH * 1.75;
        player.coolDown?.({ hard: farAway });
      });
    },
  };

  customElements.define(
    'video-section-player',
    class VideoSectionPlayer extends HTMLElement {
      connectedCallback() {
        this.videos = Array.from(this.querySelectorAll('video'));
        if (!this.videos.length) return;

        this.media = this.querySelector('.video-section__media');
        this.muteButton = this.querySelector('[data-video-mute]');
        this.fullscreenButton = this.querySelector('[data-video-fullscreen]');
        this.restartButton = this.querySelector('[data-video-restart]');
        this.playButton = this.querySelector('[data-video-play]');
        this.pauseButton = this.querySelector('[data-video-pause]');
        this.autoplay = this.dataset.autoplay === 'true';
        this.scrollPlayOnce = this.dataset.scrollPlayOnce === 'true';
        this.scrollPlay = this.dataset.scrollPlay === 'true' || this.scrollPlayOnce;
        this.playWithSound = this.dataset.playWithSound === 'true';
        this.desktopQuery = window.matchMedia('(min-width: 750px)');

        this.userPaused = false;
        this.hasEnded = false;
        this.hasStarted = false;
        this.isIntersecting = false;
        this.hasFullyLeftScreen = true;
        this.warmedUp = false;
        this.firstFrameReady = false;
        this.playRequestId = 0;

        this.videos.forEach((video) => {
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.controls = false;
          video.loop = this.scrollPlay ? false : video.loop;
          video.setAttribute('playsinline', '');
          video.setAttribute('muted', '');
          video.setAttribute('webkit-playsinline', '');
          video.removeAttribute('controls');
          if (this.scrollPlay) {
            video.removeAttribute('loop');
          }

          // Autoplay/scroll-play start with metadata; manual play stays unloaded until click
          if (this.scrollPlay || this.autoplay) {
            video.preload = 'metadata';
            video.setAttribute('preload', 'metadata');
          } else {
            video.preload = 'none';
            video.setAttribute('preload', 'none');
          }

          if (!(this.scrollPlay || this.autoplay)) {
            video.classList.add('is-ready');
          }

          if (this.dataset.alt) {
            video.setAttribute('aria-label', this.dataset.alt);
          }
        });

        this.onMuteClick = this.onMuteClick.bind(this);
        this.onFullscreenClick = this.onFullscreenClick.bind(this);
        this.onRestartClick = this.onRestartClick.bind(this);
        this.onPlayClick = this.onPlayClick.bind(this);
        this.onPauseClick = this.onPauseClick.bind(this);
        this.onVideoClick = this.onVideoClick.bind(this);
        this.onVideoEnded = this.onVideoEnded.bind(this);
        this.onPlaybackChange = this.onPlaybackChange.bind(this);
        this.onViewportScroll = this.onViewportScroll.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.muteButton?.addEventListener('click', this.onMuteClick);
        this.fullscreenButton?.addEventListener('click', this.onFullscreenClick);
        this.restartButton?.addEventListener('click', this.onRestartClick);
        this.playButton?.addEventListener('click', this.onPlayClick);
        this.pauseButton?.addEventListener('click', this.onPauseClick);

        if (!this.scrollPlay) {
          this.videos.forEach((video) => video.addEventListener('click', this.onVideoClick));
        } else {
          this.videos.forEach((video) => video.addEventListener('ended', this.onVideoEnded));
        }

        this.videos.forEach((video) => {
          video.addEventListener('play', this.onPlaybackChange);
          video.addEventListener('pause', this.onPlaybackChange);
          video.addEventListener('ended', this.onPlaybackChange);
        });

        // Only autoplay / scroll-play videos warm ahead of the viewport
        if (this.scrollPlay || this.autoplay) {
          VideoWarmup.register(this);
          window.addEventListener('scroll', this.onViewportScroll, { passive: true });
          window.addEventListener('resize', this.onViewportScroll, { passive: true });
          this.onViewportScroll();
        }

        if (typeof this.desktopQuery.addEventListener === 'function') {
          this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        } else {
          this.desktopQuery.addListener(this.onBreakpointChange);
        }

        this.syncMuteUi();
        this.syncPlaybackUi();
      }

      disconnectedCallback() {
        VideoWarmup.unregister(this);
        window.removeEventListener('scroll', this.onViewportScroll);
        window.removeEventListener('resize', this.onViewportScroll);
        if (this.viewportScrollRaf) cancelAnimationFrame(this.viewportScrollRaf);
        this.muteButton?.removeEventListener('click', this.onMuteClick);
        this.fullscreenButton?.removeEventListener('click', this.onFullscreenClick);
        this.restartButton?.removeEventListener('click', this.onRestartClick);
        this.playButton?.removeEventListener('click', this.onPlayClick);
        this.pauseButton?.removeEventListener('click', this.onPauseClick);
        this.videos?.forEach((video) => {
          video.removeEventListener('click', this.onVideoClick);
          video.removeEventListener('ended', this.onVideoEnded);
          video.removeEventListener('play', this.onPlaybackChange);
          video.removeEventListener('pause', this.onPlaybackChange);
          video.removeEventListener('ended', this.onPlaybackChange);
        });

        if (!this.desktopQuery) return;

        if (typeof this.desktopQuery.removeEventListener === 'function') {
          this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
        } else {
          this.desktopQuery.removeListener(this.onBreakpointChange);
        }
      }

      getActiveVideo() {
        return this.videos.find((video) => video.offsetParent !== null) || this.videos[0];
      }

      isFullyOffScreen() {
        const rect = this.getBoundingClientRect();
        const viewH = window.innerHeight || 0;
        return rect.bottom <= 0 || rect.top >= viewH;
      }

      isCenteredInViewport() {
        const rect = this.getBoundingClientRect();
        const viewH = window.innerHeight || 0;
        const viewW = window.innerWidth || 0;
        if (!viewH || !viewW || rect.height === 0 || rect.width === 0) return false;

        const videoCenterY = rect.top + rect.height / 2;
        const videoCenterX = rect.left + rect.width / 2;
        const verticalOk = Math.abs(videoCenterY - viewH / 2) <= viewH * 0.2;
        const horizontalOk = Math.abs(videoCenterX - viewW / 2) <= viewW * 0.35;

        return verticalOk && horizontalOk;
      }

      warmUp() {
        if (!(this.scrollPlay || this.autoplay)) return;

        const video = this.getActiveVideo();
        if (!video) return;

        // Drop Shopify poster early so Safari doesn't keep showing it over the first frame
        video.removeAttribute('poster');
        video.poster = '';

        const wasCold = !this.warmedUp || video.readyState === 0;
        video.preload = 'auto';
        video.setAttribute('preload', 'auto');

        // Safari won't resume fetching after preload=none unless load() is called again
        if (wasCold) {
          this.warmedUp = true;
          try {
            video.load();
          } catch (error) {
            // Ignore load errors.
          }
        } else {
          this.warmedUp = true;
        }

        if (!this.firstFrameReady && !this.hasStarted) {
          this.captureFirstFrame(video);
        }
      }

      coolDown({ hard = false } = {}) {
        if (!(this.scrollPlay || this.autoplay)) return;
        if (this.hasStarted && !this.isFullyOffScreen()) return;

        const video = this.getActiveVideo();
        if (!video) return;

        video.pause();
        // Never use preload=none on Safari — it often never recovers
        video.preload = hard && !isSafari ? 'none' : 'metadata';
        video.setAttribute('preload', video.preload);
        this.warmedUp = false;

        if (!hard || isSafari) return;

        this.firstFrameReady = false;
        this.hasStarted = false;
        this.hasEnded = false;
        this.hasFullyLeftScreen = true;
        this.classList.remove('is-ended');
        video.classList.remove('is-ready');
        video.dataset.capturingFrame = 'false';
        try {
          video.load();
        } catch (error) {
          // Ignore load errors while releasing.
        }
      }

      captureFirstFrame(video) {
        if (this.firstFrameReady || this.hasStarted) {
          video.classList.add('is-ready');
          return;
        }

        if (video.dataset.capturingFrame === 'true') return;
        video.dataset.capturingFrame = 'true';

        const reveal = () => {
          video.dataset.capturingFrame = 'false';
          if (this.hasStarted && !video.paused) {
            video.classList.add('is-ready');
            video.removeAttribute('poster');
            video.poster = '';
            return;
          }
          video.pause();
          video.classList.add('is-ready');
          video.removeAttribute('poster');
          video.poster = '';
          this.firstFrameReady = true;
        };

        const seekToStart = () => {
          if (this.hasStarted) {
            video.dataset.capturingFrame = 'false';
            video.classList.add('is-ready');
            return;
          }
          // Safari needs HAVE_CURRENT_DATA before seeks stick
          if (video.readyState < 2) {
            video.addEventListener('loadeddata', seekToStart, { once: true });
            return;
          }
          video.addEventListener('seeked', reveal, { once: true });
          try {
            video.currentTime = 0.001;
          } catch (error) {
            try {
              video.currentTime = 0;
            } catch (innerError) {
              reveal();
            }
          }
        };

        if (video.readyState >= 2) {
          seekToStart();
        } else {
          video.addEventListener('loadeddata', seekToStart, { once: true });
          video.addEventListener('canplay', seekToStart, { once: true });
        }

        setTimeout(() => {
          if (!video.classList.contains('is-ready')) reveal();
        }, 4000);
      }

      onViewportScroll() {
        if (!this.autoplay && !this.scrollPlay) return;
        VideoWarmup.schedule();

        if (this.viewportScrollRaf) cancelAnimationFrame(this.viewportScrollRaf);

        this.viewportScrollRaf = requestAnimationFrame(() => {
          this.viewportScrollRaf = null;
          const active = this.getActiveVideo();
          if (!active) return;

          if (this.scrollPlay) {
            this.syncScrollPlay(active);
          } else {
            this.syncAutoplay(active);
          }
        });
      }

      syncAutoplay(active) {
        const fullyOff = this.isFullyOffScreen();
        const centered = this.isCenteredInViewport();
        this.isIntersecting = !fullyOff;

        this.videos.forEach((video) => {
          if (video !== active) {
            video.pause();
            return;
          }

          if (fullyOff) {
            if (this.hasStarted || this.hasEnded) {
              this.resetToFirstFrame(video);
            }
            this.hasFullyLeftScreen = true;
            return;
          }

          if (this.userPaused) return;

          if (centered && (this.hasFullyLeftScreen || !this.hasStarted)) {
            this.hasFullyLeftScreen = false;
            this.hasStarted = true;
            this.hasEnded = false;
            this.playVideo(video, { fromStart: true });
          }
        });
      }

      syncScrollPlay(active) {
        if (this.isFullyOffScreen()) {
          if (this.hasStarted || this.hasEnded) {
            this.videos.forEach((video) => this.resetToFirstFrame(video));
          }
          this.hasFullyLeftScreen = true;
          this.isIntersecting = false;
          return;
        }

        const centered = this.isCenteredInViewport();
        this.isIntersecting = true;

        this.videos.forEach((video) => {
          if (video !== active) {
            video.pause();
            return;
          }

          if (centered && this.hasFullyLeftScreen) {
            this.hasFullyLeftScreen = false;
            this.resetAndPlay(video);
            return;
          }

          if (this.hasEnded) return;

          if (this.hasStarted && video.paused && !this.userPaused) {
            this.playVideo(video, { fromStart: false });
          }
        });
      }

      resetToFirstFrame(video) {
        this.hasStarted = false;
        this.hasEnded = false;
        this.classList.remove('is-ended');
        video.pause();

        if (!this.warmedUp) {
          video.classList.remove('is-ready');
          this.firstFrameReady = false;
          return;
        }

        const onSeeked = () => {
          if (this.hasStarted) return;
          video.pause();
          video.classList.add('is-ready');
          this.firstFrameReady = true;
        };

        video.addEventListener('seeked', onSeeked, { once: true });

        try {
          video.currentTime = 0.001;
        } catch (error) {
          try {
            video.currentTime = 0;
          } catch (innerError) {
            video.classList.add('is-ready');
          }
        }
      }

      resetAndPlay(video) {
        this.hasEnded = false;
        this.classList.remove('is-ended');
        this.hasStarted = true;
        this.playVideo(video, { fromStart: true });
      }

      onBreakpointChange() {
        const active = this.getActiveVideo();
        this.videos.forEach((video) => {
          if (video === active) return;
          video.pause();
        });

        if (this.scrollPlay || this.autoplay) {
          this.warmedUp = false;
          this.firstFrameReady = false;
          VideoWarmup.schedule();
          this.onViewportScroll();
        }
      }

      onVideoEnded(event) {
        if (!this.scrollPlay) return;
        if (event.currentTarget !== this.getActiveVideo()) return;

        this.hasEnded = true;
        this.classList.add('is-ended');
        const video = event.currentTarget;
        try {
          if (video.duration && !Number.isNaN(video.duration)) {
            video.currentTime = Math.max(video.duration - 0.05, 0);
          }
        } catch (error) {
          // Keep the ended frame as-is if seeking fails.
        }
        video.pause();
        video.classList.add('is-ready');
      }

      onRestartClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const video = this.getActiveVideo();
        if (!video) return;

        this.hasEnded = false;
        this.hasStarted = true;
        this.userPaused = false;
        this.classList.remove('is-ended');
        this.playVideo(video, { fromStart: true });
      }

      playVideo(video = this.getActiveVideo(), { fromStart = false } = {}) {
        if (!video) return;

        this.pauseOthersInGroup();

        if (this.scrollPlay || this.autoplay) {
          this.warmUp();
          VideoWarmup.schedule();
        } else {
          // Load only when the user actually presses play
          video.preload = 'auto';
          video.setAttribute('preload', 'auto');
          if (video.readyState === 0) {
            try {
              video.load();
            } catch (error) {
              // Ignore load errors.
            }
          }
        }

        if (!fromStart && !video.paused && !video.ended) {
          video.classList.add('is-ready');
          this.syncPlaybackUi();
          return;
        }

        const requestId = ++this.playRequestId;

        const startPlayback = () => {
          if (requestId !== this.playRequestId) return;

          video.classList.add('is-ready');
          video.playsInline = true;
          video.preload = 'auto';
          video.removeAttribute('poster');
          video.poster = '';
          if (!this.playWithSound) {
            video.muted = true;
          }

          const attemptPlay = () => {
            if (requestId !== this.playRequestId) return;
            const playPromise = video.play();
            if (playPromise?.catch) {
              playPromise.catch(() => {
                setTimeout(() => {
                  if (requestId !== this.playRequestId) return;
                  if (!this.playWithSound) {
                    video.muted = true;
                  }
                  video.play()?.catch?.(() => {});
                }, 200);
              });
            }
            this.syncPlaybackUi();
          };

          if (fromStart && video.currentTime > 0.05) {
            video.addEventListener('seeked', attemptPlay, { once: true });
            try {
              video.currentTime = 0;
            } catch (error) {
              attemptPlay();
            }
            return;
          }

          attemptPlay();
        };

        // Safari often needs canplay before play() will stick
        const readyEnough = isSafari ? video.readyState >= 3 : video.readyState >= 2;
        if (readyEnough) {
          startPlayback();
          return;
        }

        const onReady = () => {
          if (requestId !== this.playRequestId) return;
          startPlayback();
        };
        video.addEventListener('loadeddata', onReady, { once: true });
        video.addEventListener('canplay', onReady, { once: true });
        if (isSafari) {
          video.addEventListener('canplaythrough', onReady, { once: true });
        }
      }

      pauseOthersInGroup() {
        const group = this.closest('[data-single-video-playback]');
        if (!group) return;

        group.querySelectorAll('video-section-player').forEach((player) => {
          if (player === this || typeof player.pauseFromGroup !== 'function') return;
          player.pauseFromGroup();
        });
      }

      pauseFromGroup() {
        this.userPaused = true;
        this.playRequestId += 1;
        this.videos?.forEach((video) => {
          if (!video.paused) video.pause();
        });
        this.syncPlaybackUi();
      }

      onPlayClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const video = this.getActiveVideo();
        if (!video) return;

        this.userPaused = false;
        this.hasEnded = false;
        this.hasStarted = true;
        this.classList.remove('is-ended');
        if (this.playWithSound) {
          this.setMuted(false);
        }
        this.playVideo(video, { fromStart: false });
      }

      onPauseClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const video = this.getActiveVideo();
        if (!video) return;

        this.userPaused = true;
        video.pause();
        this.syncPlaybackUi();
      }

      onPlaybackChange() {
        this.syncPlaybackUi();
      }

      syncPlaybackUi() {
        const video = this.getActiveVideo();
        const playing = Boolean(video && !video.paused && !video.ended);
        this.classList.toggle('is-playing', playing);
        if (playing) {
          this.classList.add('has-started');
        }

        if (this.playButton) {
          this.playButton.hidden = playing;
          this.playButton.setAttribute('aria-hidden', playing ? 'true' : 'false');
        }

        if (this.pauseButton) {
          this.pauseButton.hidden = !playing;
          this.pauseButton.setAttribute('aria-hidden', playing ? 'false' : 'true');
        }
      }

      onVideoClick(event) {
        if (this.scrollPlay) return;

        const video = event.currentTarget;
        if (video.paused) {
          this.userPaused = false;
          if (this.playWithSound) {
            this.setMuted(false);
          }
          this.hasStarted = true;
          this.playVideo(video);
        } else {
          this.userPaused = true;
          video.pause();
        }
        this.syncPlaybackUi();
      }

      setMuted(muted) {
        this.videos.forEach((video) => {
          video.muted = muted;
        });
        this.syncMuteUi();
      }

      onMuteClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.setMuted(!this.getActiveVideo().muted);
      }

      syncMuteUi() {
        const muted = Boolean(this.getActiveVideo()?.muted);
        this.classList.toggle('is-unmuted', !muted);
        if (this.muteButton) {
          this.muteButton.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
          this.muteButton.setAttribute('aria-pressed', muted ? 'false' : 'true');
        }
      }

      onFullscreenClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const target = this.media || this;

        if (document.fullscreenElement) {
          document.exitFullscreen();
          return;
        }

        if (typeof target.requestFullscreen === 'function' && document.fullscreenEnabled) {
          target.requestFullscreen().catch(() => {
            this.enterVideoFullscreen();
          });
          return;
        }

        this.enterVideoFullscreen();
      }

      enterVideoFullscreen() {
        const video = this.getActiveVideo();
        if (typeof video?.webkitEnterFullscreen === 'function') {
          video.webkitEnterFullscreen();
        }
      }
    }
  );
}
